/**
 * ============================================================================
 * MIGRATION MANAGER
 * ============================================================================
 *
 * Orchestrates all data migrations for the Luxxbox Acoustic Analyzer.
 *
 * Purpose:
 * - Detects which migrations need to run based on user's data version
 * - Executes migrations in sequential order
 * - Tracks which migrations have completed
 * - Handles post-migration hooks (e.g., catalog refresh)
 *
 * How it works:
 * 1. Called by persistence plugin when loading user data
 * 2. Checks user's data version vs latest migration version
 * 3. Runs all migrations between user's version and latest
 * 4. Updates user's version number
 * 5. Saves migrated data back to localStorage
 *
 * ============================================================================
 */

import { v1_multi_product } from './v1-multi-product.js';

// ============================================================================
// REGISTERED MIGRATIONS
// ============================================================================
// Add new migrations here in version order
// Each migration should export: { version, name, description, needsMigration, migrate, postMigrate }

const migrations = [
    v1_multi_product,
    // v2_your_next_migration,
    // v3_another_migration,
];

// Get the latest migration version
const LATEST_VERSION = migrations.length > 0 ? Math.max(...migrations.map((m) => m.version)) : 0;

/**
 * Get the current version of user's data
 *
 * @param {Object} userData - User data from localStorage
 * @returns {number} - Version number (0 if never migrated)
 */
function getUserDataVersion(userData) {
    // Check for version field (added in v1)
    if (userData._migrationVersion) {
        return userData._migrationVersion;
    }

    // No version field means pre-migration data (version 0)
    return 0;
}

/**
 * Main migration orchestrator
 * Runs all necessary migrations in sequence
 *
 * @param {Object} userData - User data from localStorage
 * @returns {Object} - Migrated user data
 */
export function runMigrations(userData) {
    // Handle empty or invalid data
    if (!userData || typeof userData !== 'object') {
        console.log('[Migration Manager] No user data to migrate');
        return userData;
    }

    const currentVersion = getUserDataVersion(userData);
    console.log('[Migration Manager] User data version:', currentVersion);
    console.log('[Migration Manager] Latest migration version:', LATEST_VERSION);

    // Check if migrations are needed
    if (currentVersion >= LATEST_VERSION) {
        console.log('[Migration Manager] User data is up to date, no migrations needed');
        return userData;
    }

    console.log('[Migration Manager] Migrations needed: v' + currentVersion + ' → v' + LATEST_VERSION);

    // ========================================================================
    // RUN MIGRATIONS SEQUENTIALLY
    // ========================================================================
    // Execute each migration that hasn't been run yet
    // Migrations run in version order to ensure data consistency

    let migratedData = userData;

    for (const migration of migrations) {
        // Skip migrations that have already been applied
        if (migration.version <= currentVersion) {
            console.log('[Migration Manager] Skipping v' + migration.version + ' (' + migration.name + ') - already applied');
            continue;
        }

        try {
            // Check if this specific migration needs to run
            // Some migrations may not apply to all users
            if (migration.needsMigration && !migration.needsMigration(migratedData)) {
                console.log('[Migration Manager] Skipping v' + migration.version + ' (' + migration.name + ') - not needed');
                continue;
            }

            console.log('[Migration Manager] Running v' + migration.version + ' (' + migration.name + ')...');
            console.log('[Migration Manager] Description:', migration.description);

            // Execute the migration
            migratedData = migration.migrate(migratedData);

            // Update version after successful migration
            migratedData._migrationVersion = migration.version;

            console.log('[Migration Manager] Successfully completed v' + migration.version);
        } catch (error) {
            // Handle migration errors gracefully
            console.error('[Migration Manager] Failed to run v' + migration.version + ':', error);
            console.error('[Migration Manager] User data may be in inconsistent state');

            // Return original data to prevent corruption
            // User may need to reset their data or contact support
            return userData;
        }
    }

    console.log('[Migration Manager] All migrations complete');

    return migratedData;
}

/**
 * Run post-migration hooks
 * Called after store is initialized and data is loaded
 *
 * Why separate: Some migrations need access to the Pinia store
 * (e.g., to refresh product catalog data). The store isn't available
 * during initial migration, so these hooks run afterwards.
 *
 * @param {Object} userData - Migrated user data
 * @param {Object} store - Pinia store instance
 */
export function runPostMigrationHooks(userData, store) {
    // Handle empty or invalid data
    if (!userData || typeof userData !== 'object') {
        return;
    }

    const currentVersion = getUserDataVersion(userData);

    console.log('[Migration Manager] Running post-migration hooks...');

    // ========================================================================
    // RUN POST-MIGRATION HOOKS
    // ========================================================================
    // Execute postMigrate function for each completed migration that has one

    for (const migration of migrations) {
        // Only run post-hooks for migrations that have been applied
        if (migration.version > currentVersion) {
            continue;
        }

        // Check if migration has a post-hook
        if (migration.postMigrate && typeof migration.postMigrate === 'function') {
            try {
                console.log('[Migration Manager] Running post-hook for v' + migration.version + ' (' + migration.name + ')');
                migration.postMigrate(userData, store);
            } catch (error) {
                console.error('[Migration Manager] Post-hook failed for v' + migration.version + ':', error);
                // Don't fail the app if post-hooks fail, just log the error
            }
        }
    }

    console.log('[Migration Manager] Post-migration hooks complete');
}

/**
 * Get migration info for debugging/display
 *
 * @returns {Object} - Migration status info
 */
export function getMigrationInfo() {
    return {
        latestVersion: LATEST_VERSION,
        availableMigrations: migrations.map((m) => ({
            version: m.version,
            name: m.name,
            description: m.description,
        })),
    };
}
